import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API, money, apiErr, getToken } from "@/lib/api";
import { Button, Field, Input, Select, Badge } from "@/lib/ui";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Invoices() {
  const [settings, setSettings] = useState({ prefix: "", suffix: "", fy: "", next_number: "" });
  const [invoices, setInvoices] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/admin/invoice-settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setBank = (k, v) => setSettings((s) => ({ ...s, bank: { ...(s.bank || {}), [k]: v } }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/invoice-settings", { prefix: settings.prefix, suffix: settings.suffix, round_off: !!settings.round_off, bank: settings.bank || {} });
      setSettings(data);
      toast.success("Invoice format saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingSettings(false); }
  };

  const download = async (inv) => {
    try {
      const res = await fetch(`${API}/admin/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${getToken("admin")}` } });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${inv.number.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.number} deleted`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return invoices;
    return invoices.filter((i) =>
      (i.number || "").toLowerCase().includes(s) ||
      (i.customer?.name || "").toLowerCase().includes(s) ||
      (i.customer?.gstin || "").toLowerCase().includes(s));
  }, [invoices, q]);

  return (
    <div data-testid="invoices-page">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">GST Invoices</h1>
      <p className="mt-1 text-sm text-slate-500">Invoices are raised automatically when a payment is approved, in the customer's name with their GSTIN. Numbering resets to 1 every financial year (Apr–Mar).</p>

      {/* Numbering planner */}
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-head text-lg font-bold text-slate-900">Invoice Number Format</h3>
          <p className="mt-1 text-xs text-slate-500">Use <code className="rounded bg-slate-100 px-1">{"{FY}"}</code> anywhere to auto-insert the financial year (e.g. {settings.fy || "26-27"}). The sequence number sits between prefix and suffix.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Prefix" hint="e.g. AII/{FY}/">
              <Input data-testid="invoice-prefix-input" value={settings.prefix} onChange={(e) => setSettings((s) => ({ ...s, prefix: e.target.value }))} />
            </Field>
            <Field label="Suffix" hint="optional, e.g. /ORIG">
              <Input data-testid="invoice-suffix-input" value={settings.suffix} onChange={(e) => setSettings((s) => ({ ...s, suffix: e.target.value }))} />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="invoice-roundoff-toggle">
              <input type="checkbox" className="h-4 w-4 accent-orange-600" checked={!!settings.round_off} onChange={(e) => setSettings((s) => ({ ...s, round_off: e.target.checked }))} />
              Round off invoice total to the nearest rupee
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button data-testid="invoice-settings-save" onClick={saveSettings} disabled={savingSettings}>
              <i className="fa-solid fa-floppy-disk" /> {savingSettings ? "Saving…" : "Save Format"}
            </Button>
            <span className="text-sm text-slate-500">Next invoice number: <b className="text-orange-600" data-testid="invoice-next-number">{settings.next_number}</b></span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-6 text-slate-300">
          <h3 className="font-head text-sm font-bold uppercase tracking-wider text-slate-400">Billed From</h3>
          <p className="mt-2 font-head text-base font-extrabold text-white">AADESHVAR INNOVATIONS INDIA</p>
          <p className="mt-1 text-xs text-slate-400">81/82 Devangapet Street No.2, Near Poomarket, Coimbatore - 641001.</p>
          <p className="mt-1 text-xs text-slate-400">GSTIN: 33ADFPT9592G2Z0</p>
          <p className="text-xs text-slate-400">Ph: +91 9543217216</p>
          <p className="mt-3 text-xs text-slate-500">GST @ 18% · CGST+SGST within Tamil Nadu, IGST for other states.</p>
        </div>
      </div>

      {/* Bank details */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-head text-lg font-bold text-slate-900">Company Bank Details</h3>
            <p className="mt-1 text-xs text-slate-500">These appear on every invoice PDF. Edit and save anytime.</p>
          </div>
          <Button data-testid="bank-save" onClick={saveSettings} disabled={savingSettings} variant="outline">
            <i className="fa-solid fa-floppy-disk" /> Save Bank Details
          </Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Account Name"><Input data-testid="bank-account-name" value={settings.bank?.account_name || ""} onChange={(e) => setBank("account_name", e.target.value)} /></Field>
          <Field label="Bank Name"><Input data-testid="bank-name" value={settings.bank?.bank_name || ""} onChange={(e) => setBank("bank_name", e.target.value)} /></Field>
          <Field label="Account Number"><Input data-testid="bank-account-number" value={settings.bank?.account_number || ""} onChange={(e) => setBank("account_number", e.target.value)} /></Field>
          <Field label="IFSC Code"><Input data-testid="bank-ifsc" value={settings.bank?.ifsc || ""} onChange={(e) => setBank("ifsc", e.target.value.toUpperCase())} /></Field>
          <Field label="Branch"><Input data-testid="bank-branch" value={settings.bank?.branch || ""} onChange={(e) => setBank("branch", e.target.value)} /></Field>
          <Field label="UPI ID (optional)"><Input data-testid="bank-upi" value={settings.bank?.upi || ""} onChange={(e) => setBank("upi", e.target.value)} /></Field>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <h3 className="font-head text-lg font-bold text-slate-900">All Invoices ({invoices.length})</h3>
        <div className="ml-auto flex items-center gap-2">
          <Input data-testid="invoice-search" placeholder="Search number / customer / GSTIN" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
          <Button data-testid="invoice-manual-btn" variant="outline" onClick={() => setShowManual(true)}><i className="fa-solid fa-plus" /> Manual Invoice</Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="invoices-empty">
          <i className="fa-solid fa-file-invoice mb-2 block text-2xl" />No invoices yet. They appear here automatically once a payment is approved.
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3">Invoice No.</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">GSTIN</th>
                <th className="px-4 py-3 text-right">Taxable</th>
                <th className="px-4 py-3 text-right">GST</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{i.number}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(i.date)}</td>
                  <td className="px-4 py-3 text-slate-700">{i.customer?.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{i.customer?.gstin}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(i.taxable)}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(i.tax_total)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{money(i.total)}</td>
                  <td className="px-4 py-3"><Badge tone={i.source === "manual" ? "slate" : "green"}>{i.source === "manual" ? "Manual" : "Auto"}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`invoice-download-${i.id}`} onClick={() => download(i)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" title="Download PDF">
                        <i className="fa-solid fa-file-pdf text-red-500" /> PDF
                      </button>
                      <button data-testid={`invoice-edit-${i.id}`} onClick={() => setEditInv(i)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" title="Edit invoice">
                        <i className="fa-solid fa-pen text-slate-500" />
                      </button>
                      <button data-testid={`invoice-delete-${i.id}`} onClick={() => removeInvoice(i)} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" title="Delete invoice">
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showManual && <InvoiceFormModal defaultRoundOff={settings.round_off} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); load(); }} />}
      {editInv && <InvoiceFormModal invoice={editInv} onClose={() => setEditInv(null)} onDone={() => { setEditInv(null); load(); }} />}
    </div>
  );
}

function InvoiceFormModal({ invoice, defaultRoundOff, onClose, onDone }) {
  const editing = !!invoice;
  const [f, setF] = useState({
    customer_name: invoice?.customer?.name || "",
    customer_gstin: invoice?.customer?.gstin && invoice.customer.gstin !== "Unregistered" ? invoice.customer.gstin : "",
    customer_address: invoice?.customer?.address || "",
    customer_phone: invoice?.customer?.phone || "",
    description: invoice?.description || "",
    amount: (invoice?.gross_taxable ?? invoice?.taxable) != null ? String(invoice.gross_taxable ?? invoice.taxable) : "",
    discount_type: invoice?.discount_type || "",
    discount_value: invoice?.discount_value ? String(invoice.discount_value) : "",
    invoice_number: invoice?.number || "",
  });
  const [roundOff, setRoundOff] = useState(editing ? !!invoice.round_off_enabled : !!defaultRoundOff);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (editing) return;
    api.get("/admin/sellers").then(({ data }) => {
      setMembers((data || []).filter((s) => s.subscription && s.subscription.status === "active" && s.subscription.plan));
    }).catch(() => {});
  }, [editing]);

  const pickMember = (id) => {
    setMemberId(id);
    const m = members.find((s) => s.id === id);
    if (!m) return;
    setF((p) => ({
      ...p,
      customer_name: m.company_name || m.phone || "",
      customer_gstin: m.gst || "",
      customer_address: [m.address, m.locality, m.city].filter(Boolean).join(", "),
      customer_phone: m.phone || "",
      description: p.description || `Annual Subscription - ${m.subscription.plan}`,
    }));
  };

  const submit = async () => {
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!f.description.trim()) { toast.error("Description is required"); return; }
    if (!(Number(f.amount) > 0)) { toast.error("Enter a valid taxable amount"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        amount: Number(f.amount),
        round_off: roundOff,
        discount_type: f.discount_type || null,
        discount_value: Number(f.discount_value) || 0,
        invoice_number: (f.invoice_number || "").trim() || undefined,
      };
      if (editing) {
        const { data } = await api.put(`/admin/invoices/${invoice.id}`, payload);
        toast.success(`Invoice ${data.number} updated`);
      } else {
        const { data } = await api.post("/admin/invoices", payload);
        toast.success(`Invoice ${data.number} created`);
      }
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid={editing ? "edit-invoice-modal" : "manual-invoice-modal"}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-head text-lg font-extrabold text-slate-900">{editing ? `Edit Invoice ${invoice.number}` : "Create Manual Invoice"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-xmark" /></button>
        </div>
        <p className="mt-1 text-xs text-slate-500">GST @ 18% is added automatically (CGST+SGST for TN GSTIN starting 33, else IGST). CGST and SGST are always equal.</p>
        {!editing && (
          <div className="mt-4">
            <Field label="Pick a paid member" hint="Only members with an active paid subscription appear here. Auto-fills the details below (still editable).">
              <Select data-testid="mi-member-select" value={memberId} onChange={(e) => pickMember(e.target.value)}>
                <option value="">— Select paid member ({members.length}) —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{(m.company_name || "(no company)")} · {m.phone} · {m.subscription.plan}</option>
                ))}
              </Select>
            </Field>
          </div>
        )}
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Customer Name" required><Input data-testid="mi-name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></Field>
          <Field label="Customer GSTIN"><Input data-testid="mi-gstin" value={f.customer_gstin} onChange={(e) => set("customer_gstin", e.target.value.toUpperCase())} placeholder="Leave blank for Unregistered" /></Field>
          <Field label="Phone"><Input data-testid="mi-phone" value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></Field>
          <Field label="Taxable Amount (₹, pre-GST)" required><Input data-testid="mi-amount" type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label="Discount" hint="Only shown on the invoice when applied.">
            <Select data-testid="mi-discount-type" value={f.discount_type} onChange={(e) => set("discount_type", e.target.value)}>
              <option value="">No discount</option>
              <option value="percent">Percentage (%)</option>
              <option value="amount">Flat amount (₹)</option>
            </Select>
          </Field>
          <Field label={f.discount_type === "amount" ? "Discount ₹" : "Discount %"}>
            <Input data-testid="mi-discount-value" type="number" value={f.discount_value} onChange={(e) => set("discount_value", e.target.value)} disabled={!f.discount_type} placeholder={f.discount_type ? "" : "Select a discount type first"} />
          </Field>
          <div className="sm:col-span-2"><Field label="Address"><Input data-testid="mi-address" value={f.customer_address} onChange={(e) => set("customer_address", e.target.value)} /></Field></div>
          <div className="sm:col-span-2"><Field label="Description" required><Input data-testid="mi-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Annual Subscription - Standard" /></Field></div>
          <div className="sm:col-span-2"><Field label="Invoice Number (override)" hint={editing ? "Change the invoice number if required." : "Leave blank to auto-generate the next number."}>
            <Input data-testid="mi-invoice-number" value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder={editing ? "" : "Auto"} />
          </Field></div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="mi-roundoff">
          <input type="checkbox" className="h-4 w-4 accent-orange-600" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} />
          Round off total to the nearest rupee
        </label>
        <Button data-testid="mi-submit" className="mt-4 w-full" onClick={submit} disabled={busy}>{busy ? "Saving…" : editing ? "Save Changes" : "Create Invoice"}</Button>
      </div>
    </div>
  );
}
